import Vue from 'vue'
import type VueRouter from 'vue-router'
import type { Store } from 'vuex'
import type { ToastObject } from 'vue-toasted'
import * as Sentry from '@sentry/vue'
import axios, { anonymousAxios, cfAxios } from '@/utils/init-axios'
import { i18n } from '@/locales/i18n-setup'
import { installChunkReload } from '@/utils/chunk-reload'
import {
  createEditTracker,
  createWriteTracker,
  unsafeReloadReasons
} from '@/utils/reload-safety'
import { isRealSha, startVersionCheck } from '@/utils/version-check'

// A blocked sessionStorage throws on access; throwing from both methods turns every reload off.
const blockedStorage = {
  getItem(): string | null {
    throw new Error('sessionStorage unavailable')
  },
  setItem(): void {
    throw new Error('sessionStorage unavailable')
  }
}

function sessionStorageOrBlocked(): Pick<Storage, 'getItem' | 'setItem'> {
  try {
    return window.sessionStorage
  } catch {
    return blockedStorage
  }
}

/** Wires the chunk-error reload and the new-build check. Call it before the root Vue is created. */
export function installVersionCheck(router: VueRouter, store: Store<any>) {
  const embeddedSha = process.env.VUE_APP_COMMIT_SHA
  const storage = sessionStorageOrBlocked()
  const writes = createWriteTracker()
  ;[axios, cfAxios, anonymousAxios].forEach(writes.install)

  installChunkReload({
    router,
    buildId: embeddedSha || 'unknown',
    storage,
    writesInFlight: writes.count,
    reloadAt: fullPath => {
      const { href } = router.resolve(fullPath)
      window.history.replaceState(window.history.state, '', href)
      window.location.reload()
    }
  })

  if (!isRealSha(embeddedSha)) return

  const edits = createEditTracker(document)
  const t = (key: string) => String(i18n.t(key))
  let toast: ToastObject | null = null

  const versionCheck = startVersionCheck({
    embeddedSha,
    versionUrl: `${process.env.BASE_URL}version.json`,
    fetchImpl: window.fetch.bind(window),
    win: window,
    storage,
    unsafeReasons: () =>
      unsafeReloadReasons({
        doc: document,
        writesInFlight: writes.count,
        hasUnsavedInput: edits.hasUnsavedInput,
        routeName: () => router.currentRoute.name,
        dataStore: () => store.state.dataStore
      }),
    reload: () => window.location.reload(),
    notice: {
      show: () => {
        if (toast) return
        toast = Vue.toasted.show(t('versionCheck.available'), {
          position: 'bottom-center',
          closeOnSwipe: false,
          action: { text: t('versionCheck.reload'), onClick: onReloadClick }
        })
      },
      hide: () => {
        toast?.goAway(0)
        toast = null
      }
    },
    reportMismatch: (embedded, served) => {
      Sentry.captureMessage('A reload did not load the deployed build', {
        extra: { embeddedSha: embedded, servedSha: served }
      })
    }
  })

  function onReloadClick() {
    if (versionCheck?.reloadClicked() === 'confirm') {
      Vue.toasted.show(t('versionCheck.saveFirst'), {
        position: 'bottom-center',
        duration: 8000
      })
    }
  }
}
